import { Entity, EntityAttributeType, EntityClass, EntityAttributeValueType } from '@moderepo/mode-internal-apis';



/**
 * Get all the displayable attributes key/value and cache it. We will show it in the card
 */
export const getDisplayableAttributes = (
    entity: Entity,
    excludeAttributes: readonly string[] | undefined,
    includeAttributes: readonly string[] | undefined,
    excludeAttributeTypes: readonly EntityAttributeValueType[] | undefined,
): {
    [key: string]: string
} | undefined => {

    if (!entity.attributes) {
        return undefined;
    }

    const hasAttributeValue = Object.values(entity.attributes).find((value) => {
        // return true if value is not null or undefined. If value is string, it must be NON EMPTY string. Empty string values are ignored.
        return value !== undefined && value !== null && (typeof value !== 'string' || value.trim().length > 0);
    }) !== undefined;

    if (hasAttributeValue) {

        // Combine all the attribute key/value into 1 strings
        const displayableAttributes = Object.entries(entity.attributes).filter(([key, value]) => {
            // First, filter out attributes based on the `excludeAttributes` and `includeAttributes` settings

            // If the key IS one of the excludeAttributes then return false
            if (excludeAttributes && excludeAttributes.includes(key)) {
                return false;
            }

            // if the key IS NOT one of the includeAttributes then return false
            if (includeAttributes && !includeAttributes.includes(key)) {
                return false;
            }

            if (excludeAttributeTypes) {
                // get the type of the value. This will be 'string', 'number', 'boolean', or 'object' (which is for both Array and Object)
                const valueType = typeof value;

                if (
                    (excludeAttributeTypes.includes(EntityAttributeType.NUMBER) && valueType === 'number')
                    || (excludeAttributeTypes.includes(EntityAttributeType.STRING) && valueType === 'string')
                    || (excludeAttributeTypes.includes(EntityAttributeType.BOOLEAN) && valueType === 'boolean')
                ) {
                    return false;
                }

                if (excludeAttributeTypes.includes(EntityAttributeType.ARRAY) && (value as unknown) instanceof Array) {
                    return false;
                }

                // If Object attribute type needs to be excluded and value is an Object then return false. Array are also considered Object so
                // need to make sure the value IS NOT an array
                if (excludeAttributeTypes.includes(EntityAttributeType.OBJECT) && valueType === 'object' && !((value as unknown) instanceof Array)) {
                    return false;
                }
            }

            return true;
        });

        if (displayableAttributes.length > 0) {
            // If there are still attributes left after we filtered out not displayable attributes then merge all the displayable
            // attributes back into an object
            return displayableAttributes.reduce((result, [key, value]) => {
                // then convert each key/value pair to "key=value" string.
                if (typeof value === 'object') {
                    return {
                        ...result,
                        [key]: JSON.stringify(value),
                    };
                }
                return {
                    ...result,
                    [key]: value,
                };

            }, {});
        }
    }

    return undefined;
};

/**
 * Get the attributeKey from the Entity to return the label associated with it
 */

export const getAttributeLabel = (entityClass: EntityClass | undefined, attributeKey: string) => {
    // check to see if there is an entity and if has an attributeSchema
    if (entityClass && entityClass.attributeSchema) {
        // check if the attributeSchema has a key that matches attributeKey
        const schema = entityClass.attributeSchema.find((item) => {
            return item.key === attributeKey;
        });
        return schema?.label ?? attributeKey;
    }
    return attributeKey;
};
